import React from "react";
import {
  Button,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Modal,
  ModalBody,
  ModalHeader,
  Spinner,
  UncontrolledDropdown,
} from "reactstrap";
import { STATUS } from "../constants";

/* ------------------------------------------------------------------ */
/* Status badge                                                        */
/* ------------------------------------------------------------------ */
export const StatusBadge = ({ status }) => {
  const active = String(status) === STATUS.ACTIVE || String(status) === "1";
  return (
    <span className={`badge ${active ? "bg-success" : "bg-secondary"}`}>
      {active ? "Active" : "Inactive"}
    </span>
  );
};

/* ------------------------------------------------------------------ */
/* Counter pill                                                        */
/* ------------------------------------------------------------------ */
export const CounterPill = ({ icon, count, label }) => (
  <span className="qd-counter" title={`${count} ${label}`}>
    {icon ? <i className={`${icon} me-1`} /> : null}
    <b>{count}</b> <span className="text-muted">{label}</span>
  </span>
);

/* ------------------------------------------------------------------ */
/* Loading / empty states                                              */
/* ------------------------------------------------------------------ */
export const LoadingState = ({ label = "Loading..." }) => (
  <div className="qd-state text-center py-5">
    <Spinner color="primary" />
    <div className="text-muted mt-2">{label}</div>
  </div>
);

export const EmptyState = ({
  icon = "bx bx-folder-open",
  title = "Nothing here yet",
  description,
  action,
}) => (
  <div className="qd-state text-center py-5">
    <i className={`${icon} qd-empty-icon`} />
    <h5 className="mt-3 mb-1">{title}</h5>
    {description ? <p className="text-muted mb-3">{description}</p> : null}
    {action}
  </div>
);

export const ErrorState = ({ message, onRetry }) => (
  <div className="qd-state text-center py-5">
    <i className="bx bx-error-circle qd-empty-icon text-danger" />
    <h5 className="mt-3 mb-1">Something went wrong</h5>
    <p className="text-muted mb-3">{message}</p>
    {onRetry ? (
      <Button color="primary" outline onClick={onRetry}>
        Try again
      </Button>
    ) : null}
  </div>
);

/* ------------------------------------------------------------------ */
/* Row action menu                                                     */
/* ------------------------------------------------------------------ */
/**
 * actions: [{ key, label, icon, onClick, hidden, disabled, danger, divider }]
 */
export const ActionMenu = ({ actions = [] }) => {
  const visible = actions.filter((a) => !a.hidden);
  if (!visible.length) return null;

  return (
    <UncontrolledDropdown>
      <DropdownToggle tag="button" className="btn btn-sm btn-light qd-action-toggle">
        <i className="bx bx-dots-vertical-rounded" />
      </DropdownToggle>
      <DropdownMenu end>
        {visible.map((a) =>
          a.divider ? (
            <DropdownItem key={a.key} divider />
          ) : (
            <DropdownItem
              key={a.key}
              disabled={a.disabled}
              className={a.danger ? "text-danger" : ""}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                a.onClick && a.onClick();
              }}
            >
              {a.icon ? <i className={`${a.icon} me-2`} /> : null}
              {a.label}
            </DropdownItem>
          )
        )}
      </DropdownMenu>
    </UncontrolledDropdown>
  );
};

/* ------------------------------------------------------------------ */
/* Confirmation dialog                                                 */
/* ------------------------------------------------------------------ */
export const ConfirmDialog = ({
  isOpen,
  title = "Are you sure?",
  message,
  warning,
  confirmLabel = "Delete",
  confirmColor = "danger",
  busy = false,
  onConfirm,
  onCancel,
}) => (
  <Modal isOpen={isOpen} toggle={busy ? undefined : onCancel} centered>
    <ModalHeader toggle={busy ? undefined : onCancel}>{title}</ModalHeader>
    <ModalBody>
      <p className="mb-2">{message}</p>
      {warning ? (
        <div className="alert alert-warning mb-0" role="alert">
          <i className="bx bx-error me-1" />
          {warning}
        </div>
      ) : null}
      <div className="d-flex justify-content-end gap-2 mt-4">
        <Button color="light" onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
        <Button color={confirmColor} onClick={onConfirm} disabled={busy}>
          {busy ? <Spinner size="sm" className="me-1" /> : null}
          {confirmLabel}
        </Button>
      </div>
    </ModalBody>
  </Modal>
);

/* ------------------------------------------------------------------ */
/* Field wrapper with inline validation message                        */
/* ------------------------------------------------------------------ */
export const Field = ({ label, required, error, hint, children }) => (
  <div className="mb-3">
    {label ? (
      <label className="form-label">
        {label}
        {required ? <span className="text-danger ms-1">*</span> : null}
      </label>
    ) : null}
    {children}
    {hint && !error ? <div className="form-text">{hint}</div> : null}
    {error ? <div className="text-danger small mt-1">{error}</div> : null}
  </div>
);
